import React, { useState, useEffect, useCallback, useRef } from 'react';
import { orderStatusLabel } from '../orderStatus';
import { api } from '../api';
import SlotChip from '../components/SlotChip';
import ReadyWithPhotoButton from '../components/ReadyWithPhotoButton';
import PhotoLightbox from '../components/PhotoLightbox';
import { useCatalogLabels, singularLower } from '../catalogLabels';
import { formatOrderPackaging } from '../utils/packagingDisplay';
import { compareBySlot, compareByReadyTime } from '../deliverySlots';
import { FW_STATUS_LABELS, flowwowDeliveryLabel } from '../components/FlowwowOrderPanel';
import { openOrdersStream } from '../utils/ordersStream';
import { ordersWord } from '../utils';

const assembledAt = (o) => (o.status_history || []).find(h => h.from_status === 'new' && h.to_status !== 'cancelled')?.changed_at || '';
function compareByAssembled(a, b) {
  const ta = assembledAt(a);
  const tb = assembledAt(b);
  if (ta === tb) return 0;
  if (!ta) return 1;
  if (!tb) return -1;
  return ta.localeCompare(tb);
}

// Точки от названия до количества — глазу проще дойти до «×5» (владелец 25.10)
// Строка с миниатюрой выровнена по центру — точки чуть ниже середины, у линии букв
const DOTS_MID = <span aria-hidden="true" style={{ flex: 1, minWidth: 12, borderBottom: '1px dotted #B9C1BC', transform: 'translateY(4px)' }} />;

// Фото позиции (владелец 26.10): у букета — фото карточки (те же, что на Flowwow), у стебля — фото
// товара; миниатюра у названия, по нажатию — крупно и листается. Фото нет — значок
const ORIGIN = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
const absUrl = (url) => (!url ? null : (url.startsWith('http') ? url : `${ORIGIN}${url}`));
function Thumb({ images, size = 40, onOpen }) {
  const box = { width: size, height: size, flexShrink: 0, '--ch': '4px' };
  if (!images || !images.length) {
    return (
      <span title="Нет фото" aria-label="Нет фото" style={{ ...box, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#F4F4EF', border: '1px dashed #C1C8C4', '--stroke': '#C1C8C4', color: '#A9B1AC' }}>
        <svg width={Math.round(size * 0.45)} height={Math.round(size * 0.45)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M21 17l-5-5-8 7" /><line x1="3" y1="3" x2="21" y2="21" />
        </svg>
      </span>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(images)} title="Открыть фото" aria-label="Открыть фото"
      style={{ ...box, padding: 0, border: '1px solid #EBEDEC', '--stroke': '#EBEDEC', background: '#fff', cursor: 'zoom-in', overflow: 'hidden' }}>
      <img src={absUrl(images[0].thumb || images[0].full)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </button>
  );
}

// Порядок сборки: стебли в составе — от большего количества к меньшему; позиции заказа — от дорогих к дешёвым
const byQtyDesc = (a, b) => (b.quantity || 0) - (a.quantity || 0) || String(a.product_name || '').localeCompare(String(b.product_name || ''), 'ru');
const byPriceDesc = (a, b) => (b.price || 0) - (a.price || 0) || byQtyDesc(a, b);
// Стебли заказа — по количеству, от большего; при равном — дороже первым (владелец 26.10)
const byQtyThenPrice = (a, b) => (b.quantity || 0) - (a.quantity || 0) || (b.price || 0) - (a.price || 0)
  || String(a.product_name || '').localeCompare(String(b.product_name || ''), 'ru');

function FloristOrdersPage({ showToast }) {
  const catalogLabels = useCatalogLabels();
  const [lightbox, setLightbox] = useState(null);
  const openPhotos = (imgs, index = 0) => setLightbox({ images: imgs.map(x => ({ src: absUrl(x.full || x.thumb || x.url) })), index });
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [dates, setDates] = useState([]);
  const [ordersGrouped, setOrdersGrouped] = useState({ new: [], packaging: [], ready: [], courier: [], completed: [], cancelled: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('new');
  const [hasPackerRole, setHasPackerRole] = useState(false);
  const [packerWorkflowEnabled, setPackerWorkflowEnabled] = useState(true);
  const [assemblyTasks, setAssemblyTasks] = useState([]);
  // Последнее задание собрано — вкладка «Сборка» пропадает, экран возвращаем на «Новые»
  useEffect(() => {
    if (activeTab === 'assembly' && assemblyTasks.length === 0) setActiveTab('new');
  }, [activeTab, assemblyTasks.length]);
  const [expandedGreetings, setExpandedGreetings] = useState(() => new Set());
  // Flowwow: фото до доставки обязательно для «Цветов», без него заказ не передать
  // курьеру. Флорист снимает готовый букет прямо с телефона; повторная загрузка
  // заменяет фото. Загрузка = «собран» (внутренний статус → ready).
  const [photoControlEnabled, setPhotoControlEnabled] = useState(false);

  const toggleGreeting = (orderId) => {
    setExpandedGreetings(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  };
  const loadOrdersRef = useRef(null);
  const selectedDateRef = useRef(selectedDate);

  useEffect(() => {
    generateDates();
  }, []);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getMyOrdersByDate(selectedDate);
      console.log('[FloristOrders] Loaded orders for date', selectedDate, ':', data);
      // Проверяем есть ли у флориста роль упаковщика и включён ли режим упаковки
      setHasPackerRole(data.florist_secondary_role === 'packer');
      setPackerWorkflowEnabled(data.packer_workflow_enabled !== false);
      api.getPhotoControlEnabled().then(r => setPhotoControlEnabled(!!r.enabled)).catch(() => {});
      setOrdersGrouped(data);
    } catch (error) {
      console.error('[FloristOrders] Load error:', error);
      showToast('Ошибка загрузки заказов', 'error');
      setOrdersGrouped({ new: [], packaging: [], ready: [], courier: [], completed: [], cancelled: [] });
    } finally {
      setIsLoading(false);
    }
    // Задания на сборку букетов (не зависят от даты)
    try {
      const tasks = await api.getMyAssemblyTasks();
      setAssemblyTasks(Array.isArray(tasks) ? tasks : []);
    } catch {
      setAssemblyTasks([]);
    }
  }, [selectedDate, showToast]);

  const handleCompleteAssembly = async (taskId) => {
    try {
      await api.completeAssemblyTask(taskId);
      showToast('Букет собран');
      setAssemblyTasks(prev => prev.filter(t => t.id !== taskId));
    } catch {
      showToast('Ошибка', 'error');
    }
  };

  // Сохраняем ссылки на loadOrders и selectedDate
  useEffect(() => {
    loadOrdersRef.current = loadOrders;
  }, [loadOrders]);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate) loadOrders();
  }, [selectedDate, loadOrders]);

  // SSE: автообновление при назначении заказа или изменении статуса
  useEffect(() => {
    // Поток заказов только для сотрудников: пропуск и переподключение — в openOrdersStream
    const es = openOrdersStream();
    console.log('[FloristOrders] SSE connection opened');
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[FloristOrders] SSE event:', data, 'currentSelectedDate:', selectedDateRef.current);
        // Обновляем если это заказ на выбранную дату
        if ((data.type === 'new_order' || data.type === 'order_updated') && data.delivery_date === selectedDateRef.current) {
          console.log('[FloristOrders] Reloading orders after SSE event');
          // Небольшая задержка чтобы дать базе данных время на коммит
          setTimeout(() => {
            if (loadOrdersRef.current) {
              loadOrdersRef.current();
            }
          }, 100);
        }
      } catch (e) {
        console.error('[FloristOrders] SSE error:', e);
      }
    };
    es.onerror = (error) => {
      console.error('[FloristOrders] SSE connection error:', error);
    };
    return () => {
      console.log('[FloristOrders] SSE connection closed');
      es.close();
    };
  }, []); // Empty dependency array - connection established once

  function getTodayDate() {
    return formatDateValue(new Date());
  }

  function formatDateValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  const getStatusLabel = orderStatusLabel; // единые подписи (src/orderStatus.js)

  // Бренд-классы из styles.css (.badge-*) + иконки nv-status-*.
  function getStatusMeta(status) {
    const map = {
      new:        { cls: 'os-new',        icon: '/icons/nv-status-new.svg' },
      packaging:  { cls: 'os-packaging',  icon: '/icons/nv-package.svg' },
      ready:      { cls: 'os-ready',      icon: '/icons/nv-status-ready.svg' },
      courier:    { cls: 'os-courier',    icon: '/icons/nv-delivery.svg' },
      completed:  { cls: 'os-completed',  icon: '/icons/nv-status-completed.svg' },
    };
    return map[status] || { cls: 'os-new', icon: null };
  }

  function formatDateDisplay(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const day = date.getDate();
    const month = date.toLocaleDateString('ru-RU', { month: 'short' });
    return `${day} ${month}`;
  }

  function getDayName(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days[date.getDay()];
  }

  function generateDates() {
    const today = new Date();
    const result = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      result.push(formatDateValue(d));
    }
    setDates(result);
  }

  const handleMarkAssembled = async (orderId) => {
    try {
      await api.updateMyOrder(orderId, { status: 'packaging' });
      showToast('На упаковку');
      loadOrders();
    } catch (error) {
      showToast(error.message || 'Ошибка обновления статуса', 'error');
    }
  };

  // Режим упаковки при фото-контроле: фото снимает флорист здесь, упаковщик только упаковывает
  const handleAssembledWithPhoto = async (orderId, file) => {
    try {
      await api.addOrderPhoto(orderId, file);
      await api.updateMyOrder(orderId, { status: 'packaging' });
      showToast('На упаковку, фото сохранено');
      await loadOrders();
    } catch (error) {
      showToast(error.message || 'Ошибка', 'error');
    }
  };

  // «Собран» с фото — один путь для всех заказов (фотоконтроль или канал продаж, например
  // Flowwow: фото уходит покупателю — это делает сервер при смене статуса). Фото уже в
  // заказе (например, прошлая отправка на площадку не прошла) — новое не снимаем
  const handleReadyWithPhoto = async (orderId, file) => {
    try {
      await api.addOrderPhoto(orderId, file);
      await api.updateMyOrder(orderId, { status: 'ready' });
      showToast('Заказ собран, фото сохранено');
    } catch (error) {
      showToast(error.message || 'Ошибка', 'error');
    } finally {
      await loadOrders();
    }
  };

  const handleMarkReady = async (orderId) => {
    try {
      await api.updateMyOrder(orderId, { status: 'ready' });
      showToast('Заказ собран');
    } catch (error) {
      showToast(error.message || 'Ошибка обновления статуса', 'error');
    } finally {
      loadOrders();
    }
  };

  const newOrders = (ordersGrouped.new || []).sort((a, b) => compareBySlot(a, b) || compareByReadyTime(a, b) || a.id - b.id);

  // Выполненные (владелец 27.10): по слоту, внутри слота — по времени сборки (когда флорист перевёл
  // заказ из «Нового», история статусов); без записи — в конце слота
  const doneOrders = [
    ...(ordersGrouped.packaging || []),
    ...(ordersGrouped.ready || []),
    ...(ordersGrouped.courier || []),
    ...(ordersGrouped.completed || []),
  ].sort((a, b) => compareBySlot(a, b) || compareByAssembled(a, b) || a.id - b.id);

  const displayOrders = activeTab === 'new' ? newOrders : doneOrders;
  const todayDate = getTodayDate();
  const totalOrders = newOrders.length + doneOrders.length;

  return (
    <div>
      {/* 3-day navigation — бренд */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12, padding: '10px 0', borderBottom: '1px solid #EBEDEC' }}>
        {dates.map(date => {
          const isToday = date === todayDate;
          const isSelected = date === selectedDate;
          return (
            <button
              key={date}
              onClick={() => setSelectedDate(date)}
              style={{
                minWidth: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                padding: '8px 14px', '--ch': '8px', '--stroke': (isSelected
                  ? '#1C3D2E'
                  : (isToday ? '#B0603C' : '#D8DDDA')),
                border: isSelected
                  ? '1px solid #1C3D2E'
                  : (isToday ? '1px solid #B0603C' : '1px solid #D8DDDA'),
                background: isSelected ? '#1C3D2E' : '#fff',
                color: isSelected ? '#fff' : '#1C3D2E',
                fontWeight: isToday || isSelected ? 600 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s, --stroke 0.15s',
              }}
            >
              <span style={{ fontSize: 11, opacity: 0.85 }}>{getDayName(date)}</span>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{formatDateDisplay(date)}</span>
            </button>
          );
        })}
      </div>

      {/* Info bar — primary green */}
      <div style={{
        background: '#1C3D2E',
        color: '#fff', padding: '10px 16px', '--ch': '8px', marginBottom: 12,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <span style={{ fontSize: 13, opacity: 0.85 }}>{getDayName(selectedDate)}, {formatDateDisplay(selectedDate)}</span>
        <span style={{ fontSize: 16, fontWeight: 700 }}>
          {totalOrders} {ordersWord(totalOrders)}
        </span>
      </div>

      {/* Tabs — бренд */}
      <div className="seg staff-tabs" style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => setActiveTab('new')} className={`seg-btn${activeTab === 'new' ? ' on' : ''}`}>
          Новые ({newOrders.length})
        </button>
        <button type="button" onClick={() => setActiveTab('done')} className={`seg-btn${activeTab === 'done' ? ' on' : ''}`}>
          Выполненные ({doneOrders.length})
        </button>
        {assemblyTasks.length > 0 && (
          <button type="button" onClick={() => setActiveTab('assembly')} className={`seg-btn${activeTab === 'assembly' ? ' on' : ''}`}>
            Сборка ({assemblyTasks.length})
          </button>
        )}
      </div>

      {/* Задания на сборку букетов */}
      {activeTab === 'assembly' ? (
        assemblyTasks.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: '32px 0' }}>Нет заданий на сборку</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {assemblyTasks.map(task => (
              <div key={task.id} style={cardStyle}>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                    padding: '2px 8px', '--ch': '4px', background: '#DFF2E8', color: '#1C3D2E',
                  }}>Сборка</span>
                  {task.bouquet_id && <Thumb images={task.bouquet_images} size={40} onOpen={openPhotos} />}
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#1C3D2E' }}>{task.bouquet_name}</span>
                  {task.shelf_life_days != null && (
                    <span style={{ color: '#5E6561', fontSize: 12 }}>· срок {task.shelf_life_days} дн.</span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#909894', textTransform: 'uppercase', marginBottom: 6 }}>
                    Состав
                  </div>
                  {[...(task.components || [])].sort(byQtyDesc).map((c, i) => (
                    <div key={c.id || i} style={{ fontSize: 15, fontWeight: 500, padding: '4px 0', lineHeight: 1.4, color: '#1C3D2E', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <Thumb images={c.images} size={28} onOpen={openPhotos} />
                      <span>{c.product_name}</span>
                      {DOTS_MID}
                      <span style={{ color: '#B0603C', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>×{c.quantity}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button type="button" onClick={() => handleCompleteAssembly(task.id)} className="btn btn-primary btn-md">
                    Собрано
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : displayOrders.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#999', padding: '32px 0' }}>
          {activeTab === 'new' ? 'Нет новых заказов' : 'Нет выполненных заказов'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {displayOrders.map(order => {
            const meta = getStatusMeta(order.status);
            return (
            <div key={order.id} style={cardStyle}>
              {/* Шапка карточки */}
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#1C3D2E' }}>
                  #{order.order_type === 'subscription'
                    ? `${order.subscription_order_number || order.subscription_id}/${order.subscription_delivery_number}`
                    : (order.order_number || order.id)}
                  {order.source_platform === 'flowwow' && order.external_platform_order_id && <span style={{ fontWeight: 500, color: '#C2185B' }}> / {order.external_platform_order_id}</span>}
                  {order.order_type === 'subscription' && (
                    <img src="/icons/nv-stats-subscriptions.svg" alt="" style={{ width: 14, height: 14, verticalAlign: '-2px', marginLeft: 4 }} />
                  )}
                </span>
                <span className={`badge badge-${meta.cls}`}>
                  {meta.icon && <img src={meta.icon} alt="" />}
                  {getStatusLabel(order.status)}
                </span>
                <span style={{ color: '#5E6561', fontSize: 13, fontWeight: 600 }}>· <SlotChip order={order} /></span>
                {order.source_platform === 'flowwow' && (
                  <span style={{ padding: '3px 8px', background: '#FCE4EC', color: '#C2185B', '--ch': '5px', fontWeight: 700, fontSize: 12 }}>
                    Flowwow #{order.external_platform_order_id} · {FW_STATUS_LABELS[order.external_status] || '—'} · {flowwowDeliveryLabel(order)}
                  </span>
                )}
                {order.packer && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: '#FFE7DD', color: '#B0603C', '--ch': '5px', fontWeight: 600, fontSize: 12 }}>
                    <img src="/icons/nv-package.svg" alt="" style={{ width: 12, height: 12 }} />
                    {order.packer.name}
                  </span>
                )}
              </div>

              {/* Мета-чипы: доставка + упаковка */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: '#F4F4EF', '--ch': '6px', fontSize: 12.5, color: '#1C3D2E', fontWeight: 600 }}>
                  <img src={order.delivery_type === 'delivery' ? '/icons/nv-delivery.svg' : '/icons/nv-pickup.svg'} alt="" style={{ width: 14, height: 14 }} />
                  {order.delivery_type === 'delivery' ? 'Доставка' : 'Самовывоз'}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: '#F4F4EF', '--ch': '6px', fontSize: 12.5, color: '#1C3D2E', fontWeight: 600 }}>
                  {(() => {
                    const { label, iconSrc } = formatOrderPackaging(order);
                    return iconSrc
                      ? <><img src={iconSrc} alt="" style={{ width: 14, height: 14 }} />{label}</>
                      : label;
                  })()}
                </span>
              </div>

              {/* Состав — три секции: Букеты / Сборные букеты / Стебли */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#909894', textTransform: 'uppercase', marginBottom: 6 }}>
                  Состав
                </div>
                {(() => {
                  const allItems = order.items || [];
                  const catalogBouquets = allItems.filter(it => it.bouquet_id).sort(byPriceDesc);
                  const stemsLoose = allItems.filter(it => !it.bouquet_id && it.assembly_sequence_no == null).sort(byQtyThenPrice);
                  // Группируем стебли сборных по sequence_no
                  const asmMap = {};
                  allItems.forEach(it => {
                    if (it.assembly_sequence_no != null) {
                      const seq = it.assembly_sequence_no;
                      if (!asmMap[seq]) {
                        asmMap[seq] = {
                          sequence_no: seq,
                          florist_notes: it.assembly_florist_notes || null,
                          items: [],
                        };
                      }
                      asmMap[seq].items.push(it);
                    }
                  });
                  const customAssemblies = Object.values(asmMap).sort((a, b) => a.sequence_no - b.sequence_no);
                  customAssemblies.forEach(asm => asm.items.sort(byQtyDesc));

                  const sectionTitle = (title) => (
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: '#7E57C2', textTransform: 'uppercase', marginTop: 8, marginBottom: 4 }}>
                      {title}
                    </div>
                  );

                  const renderBouquetItem = (item, i) => {
                    const isAssembled = item.bouquet_kind === 'assembled';
                    const hasReplacement = item.bouquet_components?.some(c => c.is_replacement);
                    return (
                      <div key={`b-${i}`} style={{ padding: '4px 0', lineHeight: 1.4, color: '#1C3D2E' }}>
                        <div style={{ fontSize: 15, fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <Thumb images={item.images} size={40} onOpen={openPhotos} />
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span>{item.product_name}</span>
                            <span style={{
                              fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                              padding: '1px 5px', borderRadius: 3,
                              background: isAssembled ? '#DFF2E8' : '#F4F4EF',
                              color: isAssembled ? '#1C3D2E' : '#5E6561',
                            }}>{isAssembled ? 'Собран' : 'К сборке'}</span>
                            {hasReplacement && (
                              <span title="Состав изменён админом" style={{
                                fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                                padding: '1px 5px', borderRadius: 3, background: '#F9EBCF', color: '#7E5D00',
                              }}>Изменён</span>
                            )}
                          </span>
                          {DOTS_MID}
                          <span style={{ color: '#B0603C', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>×{item.quantity}</span>
                        </div>
                        {(item.bouquet_components?.length || 0) > 0 && (
                          <div style={{ marginTop: 4, paddingLeft: 14, borderLeft: '2px solid #DFF2E8' }}>
                            {[...item.bouquet_components].sort(byQtyDesc).map((c, j) => (
                              <div key={c.id || j} style={{ fontSize: 13, fontWeight: 500, padding: '2px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, color: c.is_replacement ? '#7E5D00' : '#5E6561' }}>
                                <Thumb images={c.images} size={24} onOpen={openPhotos} />
                                <span>{c.is_replacement && '≠ '}{c.product_name}</span>
                                {DOTS_MID}
                                <span style={{ color: '#B0603C', fontWeight: 700, whiteSpace: 'nowrap' }}>×{c.quantity * item.quantity}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  };

                  const renderStemItem = (item, i) => (
                    <div key={`s-${i}`} style={{ padding: '4px 0', lineHeight: 1.4, color: '#1C3D2E' }}>
                      <div style={{ fontSize: 15, fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <Thumb images={item.images} size={32} onOpen={openPhotos} />
                        <span>{item.product_name}</span>
                        {DOTS_MID}
                        <span style={{ color: '#B0603C', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>×{item.quantity}</span>
                      </div>
                    </div>
                  );

                  return (
                    <>
                      {catalogBouquets.length > 0 && (
                        <>
                          {sectionTitle(catalogLabels.bouquets)}
                          {catalogBouquets.map(renderBouquetItem)}
                        </>
                      )}
                      {customAssemblies.length > 0 && (
                        <>
                          {sectionTitle(`Сборные ${catalogLabels.bouquets.toLowerCase()}`)}
                          {customAssemblies.map(asm => (
                            <div key={`asm-${asm.sequence_no}`} style={{ marginBottom: 8, paddingLeft: 8, borderLeft: '2px solid #7E57C2' }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#4A148C', marginBottom: 2 }}>
                                Сборный {singularLower(catalogLabels.bouquets)} {asm.sequence_no}
                              </div>
                              {asm.florist_notes && (
                                <div style={{ fontSize: 12, fontStyle: 'italic', color: '#7E57C2', marginBottom: 4 }}>
                                  {asm.florist_notes}
                                </div>
                              )}
                              {asm.items.map(renderStemItem)}
                            </div>
                          ))}
                        </>
                      )}
                      {stemsLoose.length > 0 && (
                        <>
                          {sectionTitle(catalogLabels.products)}
                          {stemsLoose.map(renderStemItem)}
                        </>
                      )}
                    </>
                  );
                })()}

                {order.odd_quantity && (
                  <span className="badge badge-info" style={{ marginTop: 8 }}>
                    <img src="/icons/nv-odd.svg" alt="" />
                    Нечётное кол-во
                  </span>
                )}
              </div>

              {/* Фото собранного — то, что загрузил флорист; здесь только смотреть, не менять (владелец 26.10) */}
              {(order.photos || []).length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#909894', textTransform: 'uppercase' }}>Фото сборки</span>
                  {order.photos.map((ph, k) => (
                    <button key={ph.id || k} type="button" onClick={() => openPhotos(order.photos, k)} title="Открыть фото" aria-label="Открыть фото сборки"
                      style={{ width: 56, height: 56, padding: 0, border: '1px solid #EBEDEC', '--ch': '5px', '--stroke': '#EBEDEC', background: '#fff', cursor: 'zoom-in', overflow: 'hidden' }}>
                      <img src={absUrl(ph.url)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </button>
                  ))}
                </div>
              )}

              {/* Admin comment — амбер плашка */}
              {order.admin_comment && (
                <div style={{
                  marginTop: 10, background: '#F9EBCF', borderLeft: '3px solid #7E5D00',
                  padding: '8px 12px', '--ch': '6px', fontSize: 13
                }}>
                  <strong style={{ color: '#7E5D00', fontWeight: 700 }}>Администратор:</strong>
                  <div style={{ marginTop: 3, color: '#1C3D2E', fontWeight: 500 }}>{order.admin_comment}</div>
                </div>
              )}

              {/* Greeting — раскрываемый блок */}
              {order.greeting && (() => {
                const isOpen = expandedGreetings.has(order.id);
                return (
                  <div style={{ marginTop: 8, background: '#DFF2E8', border: '1px solid #1C3D2E', '--ch': '6px', '--stroke': '#1C3D2E', color: '#1C3D2E', wordBreak: 'break-word', overflow: 'hidden' }}>
                    <button
                      onClick={() => toggleGreeting(order.id)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer',
                        fontFamily: 'inherit', color: '#1C3D2E', fontWeight: 700, fontSize: 13,
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <img src="/icons/mail.svg" alt="" style={{ width: 14, height: 14 }} />
                        Поздравление
                      </span>
                      <span style={{ fontSize: 11, opacity: 0.7, transition: 'transform 0.15s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}>▾</span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: '0 12px 10px', fontStyle: 'italic', fontSize: 13 }}>
                        «{order.greeting}»
                      </div>
                    )}
                  </div>
                );
              })()}

              {order.source_platform === 'flowwow' && !['completed', 'cancelled'].includes(order.status) && (() => {
                const ext = order.external_status;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, padding: '10px 12px', background: '#FCE4EC', '--ch': '6px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 160, fontSize: 12.5, color: '#1C3D2E' }}>
                      {order.photo_before_at
                        ? <>✓ Собран, фото ушло покупателю {new Date(order.photo_before_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                            {order.delivery_type === 'delivery' ? ' · везёт наш курьер' : order.external_delivery_type === 4 ? ' · покупатель заберёт сам, выдаёт админ' : ' · заберёт курьер Flowwow'}</>
                        : ext === 1
                          ? 'Ждёт принятия на Flowwow'
                          : 'Flowwow: «Собран» = фото готового заказа, оно уйдёт покупателю. Не отправилось — нажмите «Собран» ещё раз'}
                    </div>
                  </div>
                );
              })()}

              {activeTab === 'new' && order.status === 'new' && (() => {
                // Шаги — из ядра статусов (status_steps): у заказа Flowwow нет «На упаковке»,
                // флорист сразу ставит «Собран». Фото нужно при фотоконтроле или если требует канал;
                // уже есть в заказе — без нового снимка
                const steps = order.status_steps;
                // Канал ещё не дал собирать (заказ не принят на площадке) — шагов дальше «Новый» нет
                if (steps && !steps.includes('ready') && !steps.includes('packaging')) return null;
                const hasPackStep = packerWorkflowEnabled && (!steps || steps.includes('packaging'));
                const needPhoto = (photoControlEnabled || order.channel_requires_photo) && !(order.photos || []).length;
                return (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {hasPackStep && (
                    <ReadyWithPhotoButton needPhoto={needPhoto} label="На упаковку"
                      onPhoto={(file) => handleAssembledWithPhoto(order.id, file)}
                      onReady={() => handleMarkAssembled(order.id)}
                      style={actionBtnStyle} />
                  )}
                  {(hasPackerRole || !hasPackStep) && (
                    <ReadyWithPhotoButton needPhoto={needPhoto}
                      onPhoto={(file) => handleReadyWithPhoto(order.id, file)}
                      onReady={() => handleMarkReady(order.id)}
                      style={hasPackStep ? actionBtnSecondaryStyle : actionBtnStyle} />
                  )}
                </div>
                );
              })()}
            </div>
            );
          })}
        </div>
      ))}
      {lightbox && <PhotoLightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}


const cardStyle = {
  background: '#fff', '--ch': '8px', '--stroke': '#EBEDEC', padding: 14,
  border: '1px solid #EBEDEC',
};

const actionBtnStyle = {
  background: '#1C3D2E', color: '#fff', border: 'none', '--ch': '6px',
  padding: '9px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit', transition: 'background 0.15s',
};
const actionBtnSecondaryStyle = {
  ...actionBtnStyle,
  background: '#fff', color: '#1C3D2E', border: '1px solid #1C3D2E', '--stroke': '#1C3D2E',
};

export default FloristOrdersPage;
